import { MediaItem, normalizeMedia } from '@/types/media.types';
import { hasDayAnchor } from './day-axis';

export type ActivityType =
  | 'accommodation'
  | 'activity'
  | 'flight'
  | 'transportation'
  | 'food-drinks'
  | 'events'
  | 'notes';

/**
 * Stato di prenotazione presso il fornitore (tracciamento interno del TA).
 * Persistito su itinerary_activities.supplier_status (enum DB public.supplier_status).
 * Visibile/modificabile solo dal TA e solo quando l'itinerario è pagato (status=completed).
 * `undefined`/`null` = non ancora tracciato.
 */
export type SupplierStatus =
  | 'contacted'
  | 'optioned'
  | 'confirmed'
  | 'partially_paid'
  | 'paid';

export const SUPPLIER_STATUS_VALUES: SupplierStatus[] = [
  'contacted',
  'optioned',
  'confirmed',
  'partially_paid',
  'paid',
];

export interface ActivityDocumentation {
  name: string;
  url: string;
}

// Base activity interface
export interface BaseActivity {
  id: string;
  title: string;
  description: string;
  location: string;
  type: ActivityType;
  /** Data reale dell'attività. Vuota sui template, che usano l'intervallo di giorni. */
  date: string;
  /**
   * Intervallo di giorni relativi 1-based nei template, estremi inclusi: chi dura un
   * giorno solo ha start uguale a end. Alternativo a `date`, mai insieme.
   */
  start_day_index?: number | null;
  end_day_index?: number | null;
  /** Sola lettura: a database è generata da `start_day_index`. Non scriverla. */
  readonly day_index?: number | null;
  include_price?: boolean;
  inOrOutActivity?: 'in' | 'out';
  is_multi_day?: boolean;
  /** Stato fornitore (interno TA, visibile solo a itinerario pagato). Vedi SupplierStatus. */
  supplier_status?: SupplierStatus | null;
}

// Specialized activity interfaces
export interface RateHawkBookingMetadata {
  ratehawk_rooms?: number;
  ratehawk_guests_per_room?: Array<{ adults: number; childrenAges: number[] }>;
  /** Raw ETG metapolicy_struct for the booked hotel, captured at booking time. */
  hotel_metapolicy_struct?: Record<string, unknown> | null;
  /** Free-text policy notes from ETG (may contain inline <a> HTML tags). */
  hotel_metapolicy_extra_info?: string | null;
  /** Tax breakdown of the selected rate (city tax, occupancy tax, etc.). */
  taxes?: Array<{ name: string; amount: string; currency_code: string; included_by_supplier: boolean }> | null;
}

export interface AccommodationActivity extends BaseActivity {
  type: 'accommodation';
  link?: string;
  address: string;
  start_date: string;
  end_date: string;
  check_in_time: string;
  check_out_time: string;
  check_in_note: string;
  cost: number;
  refundable_rate: boolean;
  price_tag: string;
  media: MediaItem[];
  documentation: ActivityDocumentation[];
  metadata?: RateHawkBookingMetadata;
}

export interface ActivityActivity extends BaseActivity {
  type: 'activity';
  link?: string;
  address: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  cost: number;
  media: MediaItem[];
  documentation: ActivityDocumentation[];
}

export interface FlightActivity extends BaseActivity {
  type: 'flight';
  link?: string;
  address: string;
  travels: {
    flightNumber: string;
    airline: string;
    from: string;
    to: string;
    link: string;
    fromDate: string;
    fromTime: string;
    toDate: string;
    toTime: string;
    /** Giorni relativi sui template, convertiti in date al collegamento alla richiesta. */
    fromDayIndex?: number | null;
    toDayIndex?: number | null;

    // --- Campi popolati dal lookup del codice volo (edge function
    // get-flight-information + tabella airlines). Tutti opzionali: una tratta
    // compilata a mano non li ha, e il TD puo' comunque modificare a mano i campi
    // principali dopo aver importato il volo. `travels` e' una colonna JSONB,
    // quindi non serve alcuna migration per conservarli.
    /** Codice IATA della compagnia (es. "FR"). */
    airlineIata?: string;
    /** Logo della compagnia: viene dalla nostra tabella `airlines`, non dal provider. */
    airlineLogoUrl?: string;
    /** Colore di brand della compagnia, usato dalla preview e dalle card. */
    airlineColor?: string;
    /** Codice IATA dell'aeroporto di partenza (es. "BGY"). */
    fromCode?: string;
    /** Codice IATA dell'aeroporto di arrivo. */
    toCode?: string;
    fromTerminal?: string;
    fromGate?: string;
    toTerminal?: string;
    toGate?: string;
    /** Stato del volo dichiarato dal provider (scheduled, active, landed, cancelled...). */
    flightStatus?: string;
    /** Modello dell'aeromobile, quando il provider lo conosce. */
    aircraftModel?: string;
  }[];
  cost: number;
  documentation: ActivityDocumentation[];
}

export interface TransportationActivity extends BaseActivity {
  type: 'transportation';
  link?: string;
  transport_type: 'train' | 'bus' | 'car' | 'taxi' | 'metro' | 'other';
  company: string;
  travels: {
    address: string;
    date: string;
    time: string;
    /** Giorno relativo sui template, convertito in data al collegamento alla richiesta. */
    dayIndex?: number | null;
  }[];
  cost: number;
  documentation: ActivityDocumentation[];
}

export interface FoodDrinksActivity extends BaseActivity {
  type: 'food-drinks';
  link: string;
  media: MediaItem[];
  documentation: ActivityDocumentation[];
  start_date?: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  address?: string;
}

export interface EventsActivity extends BaseActivity {
  type: 'events';
  link?: string;
  address: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  cost: number;
  media: MediaItem[];
  documentation: ActivityDocumentation[];
}

export interface NotesActivity extends BaseActivity {
  type: 'notes';
  link: string;
  media: MediaItem[];
  documentation: { name: string; url: string }[];
}

// Helper type for creating activities
export type CreateActivityData<T extends ActivityType> =
  T extends 'accommodation' ? Partial<AccommodationActivity> :
  T extends 'activity' ? Partial<ActivityActivity> :
  T extends 'flight' ? Partial<FlightActivity> :
  T extends 'transportation' ? Partial<TransportationActivity> :
  T extends 'food-drinks' ? Partial<FoodDrinksActivity> :
  T extends 'events' ? Partial<EventsActivity> :
  T extends 'notes' ? Partial<NotesActivity> :
  Partial<BaseActivity>;

// ==================== CLASSES ====================

// Base Activity class with all possible fields
export class BaseActivityClass implements BaseActivity {
    validateActivity(): boolean {
      // Un'attività è ancorata a una data (itinerario collegato) o a un giorno (template).
      if (!this.id || !this.title || !this.type || !hasDayAnchor(this)) {
        console.warn('[validateActivity] BaseActivity missing required fields', this, { id: this.id, title: this.title, location: this.location, type: this.type, date: this.date, start_day_index: this.start_day_index });
        return true; // NO BLOCK
      }
      return true;
    }
  // Base fields (sempre presenti)
  id: string;
  title: string;
  description: string;
  location: string;
  type: ActivityType;
  date: string;
  start_day_index?: number | null;
  end_day_index?: number | null;
  /** Sola lettura: generata a database da `start_day_index`. */
  readonly day_index?: number | null;
  include_price?: boolean;
  inOrOutActivity: 'in' | 'out' = 'in';
  is_multi_day?: boolean;
  supplier_status?: SupplierStatus | null;

  // Accommodation fields
  address?: string;
  start_date?: string;
  end_date?: string;
  check_in_time?: string;
  check_out_time?: string;
  check_in_note?: string;
  refundable_rate?: boolean;
  price_tag?: string;

  // Activity & Events fields
  start_time?: string;
  end_time?: string;

  // Transportation fields
  transport_type?: 'train' | 'bus' | 'car' | 'taxi' | 'metro' | 'other';
  company?: string;

  // Common fields
  link?: string;
  cost?: number;
  media?: MediaItem[];
  documentation?: ActivityDocumentation[];
  metadata?: Record<string, unknown>;

  // Flight & Transportation travels
  travels?: Array<{
    flightNumber?: string;
    airline?: string;
    from?: string;
    to?: string;
    link?: string;
    fromDate?: string;
    fromTime?: string;
    toDate?: string;
    toTime?: string;
    address?: string;
    date?: string;
    time?: string;
    dayIndex?: number | null;
    fromDayIndex?: number | null;
    toDayIndex?: number | null;
    // Campi del lookup volo (vedi FlightActivity.travels): restano qui perche'
    // BaseActivityClass e' il tipo che attraversa il salvataggio verso il DB —
    // ometterli li farebbe cadere nel round-trip modale → builder → update-itinerary.
    airlineIata?: string;
    airlineLogoUrl?: string;
    airlineColor?: string;
    fromCode?: string;
    toCode?: string;
    fromTerminal?: string;
    fromGate?: string;
    toTerminal?: string;
    toGate?: string;
    flightStatus?: string;
    aircraftModel?: string;
  }>;

  constructor(data: ItineraryActivity | Partial<ItineraryActivity>) {
    Object.assign(this, data);
    // Le attività salvate prima del modello MediaItem hanno `media: string[]`.
    // Normalizzando qui — unico punto di costruzione di tutte le attività — i
    // componenti di rendering ricevono sempre MediaItem[] e non devono difendersi.
    // Il guard su `undefined` evita di iniettare `media: []` sui tipi che non lo
    // prevedono (flight, transportation), alterandone il payload di salvataggio.
    if (data.media !== undefined) {
      this.media = normalizeMedia(data.media);
    }
    this.id = data.id || crypto.randomUUID();
    this.inOrOutActivity = (data as any).inOrOutActivity || 'in';
    // Sui template l'estensione multi-giorno si legge dagli indici, non dalle date.
    this.is_multi_day = !!(
      (this.start_date && this.end_date && this.start_date !== this.end_date) ||
      (this.start_day_index && this.end_day_index && this.start_day_index !== this.end_day_index)
    );
  }

  isMultiDay(): boolean {
    return this.is_multi_day || false;
  }
}

// Specialized activity classes
export class AccommodationActivityClass extends BaseActivityClass {
    validateActivity(): boolean {
      // Call base checks
      if (!super.validateActivity()) return false;
      // TODO: Add accommodation-specific checks
      return true;
    }
  declare type: 'accommodation';
  
  constructor(data: Partial<AccommodationActivity>) {
    super(data as Partial<BaseActivityClass>);
  }
}

export class ActivityActivityClass extends BaseActivityClass {
    validateActivity(): boolean {
      if (!super.validateActivity()) return false;
      // TODO: Add activity-specific checks
      return true;
    }
  declare type: 'activity';
  
  constructor(data: Partial<ActivityActivity>) {
    super(data as Partial<BaseActivityClass>);
  }
}

export class FlightActivityClass extends BaseActivityClass {
    validateActivity(): boolean {
      if (!super.validateActivity()) return false;
      // TODO: Add flight-specific checks
      return true;
    }
  declare type: 'flight';
  
  constructor(data: Partial<FlightActivity>) {
    super(data as Partial<BaseActivityClass>);
  }
}

export class TransportationActivityClass extends BaseActivityClass {
    validateActivity(): boolean {
      if (!super.validateActivity()) return false;
      // TODO: Add transportation-specific checks
      return true;
    }
  declare type: 'transportation';
  
  constructor(data: Partial<TransportationActivity>) {
    super(data as Partial<BaseActivityClass>);
  }
}

export class FoodDrinksActivityClass extends BaseActivityClass {
    validateActivity(): boolean {
      if (!super.validateActivity()) return false;
      // TODO: Add food-drinks-specific checks
      return true;
    }
  declare type: 'food-drinks';
  
  constructor(data: Partial<FoodDrinksActivity>) {
    super(data as Partial<BaseActivityClass>);
  }
}

export class EventsActivityClass extends BaseActivityClass {
    validateActivity(): boolean {
      if (!super.validateActivity()) return false;
      // TODO: Add events-specific checks
      return true;
    }
  declare type: 'events';
  
  constructor(data: Partial<EventsActivity>) {
    super(data as Partial<BaseActivityClass>);
  }
}

export class NotesActivityClass extends BaseActivityClass {
    validateActivity(): boolean {
      if (!super.validateActivity()) return false;
      // TODO: Add notes-specific checks
      return true;
    }
  declare type: 'notes';
  
  constructor(data: Partial<NotesActivity>) {
    super(data as Partial<BaseActivityClass>);
  }
}

// Union type for all activity instances (classes)
export type ItineraryActivity =
  | AccommodationActivityClass
  | ActivityActivityClass
  | FlightActivityClass
  | TransportationActivityClass
  | FoodDrinksActivityClass
  | EventsActivityClass
  | NotesActivityClass;

// Factory class to create the correct Activity subclass based on type
export class ActivityFactory {
  static create(data: ItineraryActivity): ItineraryActivity {
    switch (data.type) {
      case 'accommodation':
        return new AccommodationActivityClass(data as AccommodationActivity);
      case 'activity':
        return new ActivityActivityClass(data as ActivityActivity);
      case 'flight':
        return new FlightActivityClass(data as FlightActivity);
      case 'transportation':
        return new TransportationActivityClass(data as TransportationActivity);
      case 'food-drinks':
        return new FoodDrinksActivityClass(data as FoodDrinksActivity);
      case 'events':
        return new EventsActivityClass(data as EventsActivity);
      case 'notes':
        return new NotesActivityClass(data as NotesActivity);
      default:
        return new BaseActivityClass(data);
    }
  }
}
